import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { RoleModel } from '../../role.model';
import { AppTextService } from '@fi-shared/app-text';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { ApplicationSettingsModel } from '../../application-settings.model';
import { AccessRightType, ApplicationSettingsTypeGroup } from '@fi-shared/types';
import { RoleHelperService } from '../../services/role-helper.service';
import { CustomButtonModel } from '@fi-app/core/custom-button.model';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';

export interface IAccessTypeChange {
    applicationSetting: ApplicationSettingsModel;
    accessRightType: AccessRightType;
}

@Component({
    selector: 'fi-role-rights-cards',
    templateUrl: './role-rights-cards.component.html',
    styleUrls: ['./role-rights-cards.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-role-rights-cards' },
    providers: [{ provide: FiComponentsContainer, useExisting: RoleRightsCardsComponent }],
    standalone: false,
})
export class RoleRightsCardsComponent extends FiComponentsContainer implements OnInit, OnDestroy {
    @Input() role: RoleModel;
    @Input() isEditMode: boolean = false;
    @Input() customButtons: CustomButtonModel[];
    @Input() roleApplicationSettingGroups: {
        [key: string]: ApplicationSettingsModel[];
    };
    @Output() onApplicationSettingAccessTypeChange = new EventEmitter<IAccessTypeChange>();
    @Output() onCustomButtonToggleChange = new EventEmitter<void>();
    @Output() onExtendedRightsClick = new EventEmitter<void>();
    @Output() onSaveClick = new EventEmitter<void>();
    @Output() onBackClick = new EventEmitter<void>();

    isRoleDataChanged: boolean = false;

    constructor(
        private appTextService: AppTextService,
        private contentLanguagesService: ContentLanguagesService,
        private cdr: ChangeDetectorRef,
        private roleHelperService: RoleHelperService,
        private detectDevicesService: DetectDevicesService,
    ) {
        super();
    }

    ngOnInit() {
        this.subscribeToRoleChanges();
    }

    private subscribeToRoleChanges() {
        this.roleHelperService
            .isRoleDataChanged$()
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((value: boolean) => {
                this.isRoleDataChanged = value;
                this.cdr.markForCheck();

                this.updateHasAccessToAll();
            });
    }

    private updateHasAccessToAll() {
        // https://chainformation.atlassian.net/browse/FI-5481
        if (this.roleHelperService.hasAdministrationOnCoreGlobal(this.role.RoleApplicationSettings)) {
            // "Allowed to view and manage users and units in any business area" should be automatically enabled and can't be disabled for roles with Administrate right for Global level of Our organisation function.
            this.role.ExtendedRoleRight.HasAccessToAll = true;
        } else if (this.roleHelperService.hasNoRightsOnCore(this.role.RoleApplicationSettings)) {
            // "Allowed to view and manage users and units in any business area" should be automatically disabled and can't be disabled if role has no access to "Our organisation" module
            this.role.ExtendedRoleRight.HasAccessToAll = false;
        }
    }

    getApplicationSettingsGroupKeysByChecked(isChecked: boolean): string[] {
        return Object.keys(this.roleApplicationSettingGroups)
            .filter((key: string) => {
                return (
                    this.roleHelperService.isModuleRightChecked(this.roleApplicationSettingGroups[key]) === isChecked &&
                    +key !== ApplicationSettingsTypeGroup.Other
                );
            })
            .sort((key: string, nextKey: string) => {
                const groupLabels = this.roleHelperService.getApplicationSettingsTypeGroupLabels(+key);
                const nextGroupLabels = this.roleHelperService.getApplicationSettingsTypeGroupLabels(+nextKey);
                if (groupLabels?.name > nextGroupLabels?.name) return 1;

                if (groupLabels?.name < nextGroupLabels?.name) return -1;

                return 0;
            });
    }

    getCustomButtonsBySelected(isSelected: boolean): CustomButtonModel[] {
        return this.customButtons.filter((customButton: CustomButtonModel) => customButton.IsSelected === isSelected);
    }

    isEnabledModulesShown(): boolean {
        return (
            Object.keys(this.roleApplicationSettingGroups).some((key: string) => {
                return this.roleHelperService.isModuleRightChecked(this.roleApplicationSettingGroups[key]);
            }) || this.customButtons.some((customButton: CustomButtonModel) => customButton.IsSelected)
        );
    }

    isDisabledModulesShown(): boolean {
        return (
            Object.keys(this.roleApplicationSettingGroups).some((key: string) => {
                return !this.roleHelperService.isModuleRightChecked(this.roleApplicationSettingGroups[key]);
            }) || this.customButtons.some((customButton: CustomButtonModel) => !customButton.IsSelected)
        );
    }

    goToExtendedRights() {
        this.onExtendedRightsClick.emit();
    }

    back() {
        this.onBackClick.emit();
    }

    save() {
        this.onSaveClick.emit();
    }

    isMobilePhone(): boolean {
        return this.detectDevicesService.isMobilePhone();
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
